import type { ImportWarning } from '@/api/import'

export interface GroupedImportWarningSection {
  id: string
  title: string
  bullets: string[]
}

const MAX_VISIBLE_BULLETS = 50

function extractQuotedEventTitle(message: string): string | null {
  const forEvent = message.match(/for event "([^"]*)"/)
  if (forEvent?.[1] != null && forEvent[1].trim() !== '') return forEvent[1].trim()
  const bare = message.match(/event "([^"]*)"/)
  return bare?.[1]?.trim() ?? null
}

function extractDocumentSubject(message: string): string | null {
  let m = message.match(/^Could not import documents for (.+?):/)
  if (m?.[1]) return m[1].trim()
  m = message.match(/^Imported \d+ of \d+ documents for (.+?);/)
  return m?.[1]?.trim() ?? null
}

function uniquePreserveOrder(lines: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const line of lines) {
    const key = line.trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(line)
  }
  return out
}

function truncateBullets(lines: string[]): string[] {
  if (lines.length <= MAX_VISIBLE_BULLETS) return lines
  const shownCount = MAX_VISIBLE_BULLETS - 1
  const hidden = lines.length - shownCount
  return [...lines.slice(0, shownCount), `…and ${hidden} more`]
}

export function groupImportWarnings(
  warnings: ImportWarning[],
): GroupedImportWarningSection[] {
  const eventUnavailable: string[] = []
  const eventPartialLines: string[] = []
  const docUnavailable: string[] = []
  const docPartial: string[] = []
  const teamCommunicators: string[] = []
  const teamProposals: string[] = []
  const teamVotes: string[] = []
  const teamVotingStatus: string[] = []
  const other: string[] = []

  for (const w of warnings) {
    const { code, message } = w

    if (code.startsWith('team_communicator_')) {
      teamCommunicators.push(message)
      continue
    }
    if (code.startsWith('team_proposal_')) {
      teamProposals.push(message)
      continue
    }
    if (code.startsWith('team_vote_')) {
      teamVotes.push(message)
      continue
    }
    if (code.startsWith('team_voting_status_')) {
      teamVotingStatus.push(message)
      continue
    }

    if (code === 'event_media_unavailable') {
      eventUnavailable.push(extractQuotedEventTitle(message) ?? message)
      continue
    }
    if (code === 'event_media_partial') {
      eventPartialLines.push(message)
      continue
    }
    if (code.endsWith('_documents_unavailable')) {
      docUnavailable.push(extractDocumentSubject(message) ?? message)
      continue
    }
    if (code.endsWith('_documents_partial')) {
      docPartial.push(message)
      continue
    }
    other.push(message)
  }

  const sections: GroupedImportWarningSection[] = []

  if (eventUnavailable.length) {
    sections.push({
      id: 'event_media_unavailable',
      title: `Event media missing (${eventUnavailable.length})`,
      bullets: truncateBullets(uniquePreserveOrder(eventUnavailable)),
    })
  }

  if (eventPartialLines.length) {
    sections.push({
      id: 'event_media_partial',
      title: `Partial event media imports (${eventPartialLines.length})`,
      bullets: truncateBullets(uniquePreserveOrder(eventPartialLines)),
    })
  }

  if (docUnavailable.length) {
    sections.push({
      id: 'documents_unavailable',
      title: `Documents missing (${docUnavailable.length})`,
      bullets: truncateBullets(uniquePreserveOrder(docUnavailable)),
    })
  }

  if (docPartial.length) {
    sections.push({
      id: 'documents_partial',
      title: `Partial document imports (${docPartial.length})`,
      bullets: truncateBullets(uniquePreserveOrder(docPartial)),
    })
  }

  if (teamCommunicators.length) {
    sections.push({
      id: 'team_communicators',
      title: `Team communicators (${teamCommunicators.length})`,
      bullets: truncateBullets(uniquePreserveOrder(teamCommunicators)),
    })
  }

  if (teamProposals.length) {
    sections.push({
      id: 'team_strategy_proposals',
      title: `Strategy proposals (${teamProposals.length})`,
      bullets: truncateBullets(uniquePreserveOrder(teamProposals)),
    })
  }

  if (teamVotes.length) {
    sections.push({
      id: 'team_strategy_votes',
      title: `Strategy votes (${teamVotes.length})`,
      bullets: truncateBullets(uniquePreserveOrder(teamVotes)),
    })
  }

  if (teamVotingStatus.length) {
    sections.push({
      id: 'team_voting_statuses',
      title: `Voting status (${teamVotingStatus.length})`,
      bullets: truncateBullets(uniquePreserveOrder(teamVotingStatus)),
    })
  }

  if (other.length) {
    sections.push({
      id: 'other',
      title: `Other notices (${other.length})`,
      bullets: truncateBullets(uniquePreserveOrder(other)),
    })
  }

  return sections
}
